/**
 * components/hazard/InspectionTypeSection.js — ХАБ-ын шалгалтын төрөл.
 *
 * Үзлэгийн төрлөөс хамаарч асуумжийн бүтэц, дотоод урсгал өөрчлөгдөнө.
 *
 * Props:
 *   value     : string (төрлийн key)
 *   onChange  : (key) => void
 *   options   : Optional — өөр санал болгох жагсаалт
 */
import React from "react";
import { Select } from "antd";
import { Card, SectionTitle } from "./common";
import { xabRefApi, xabInspectionObjectApi } from "src/api/xabApi";
import CriticalControlSheet from "src/components/inspection/CriticalControlSheet";

// ⚠ Icon нэрс — төслийн bundle-д Font Awesome FREE 5.11.2 (public/plugins/
// fontawesome-free) орсон. FA6-д шинээр нэрлэгдсэн icon (fa-shield-alt,
// fa-layer-group, fa-calendar-times г.м.) энд ХАРАГДАХГҮЙ тул FA5 нэрийг ашиглана.
export const INSPECTION_TYPES = [
  {
    key: "critical_control",
    label: "Чухал хяналт",
    desc: "15 критик хяналтын асуумжаар",
    icon: "fa-shield-alt",
    color: "#A32D2D",
  },
  {
    key: "level_by_level",
    label: "Шат шатны үзлэг",
    desc: "1-4 шатаар явагдах ерөнхий үзлэг",
    icon: "fa-layer-group",
    color: "#0EA5E9",
  },
  {
    key: "targeted",
    label: "Зорилтот",
    desc: "Тодорхой эрсдэл / асуудалд чиглэсэн",
    icon: "fa-crosshairs",
    color: "#EA580C",
  },
  /* ⚠ «Төлөвлөгөөт» ЭНД БАЙХГҮЙ нь санаатай. ХЭМАБМТ-06 журам 1.5.1-1.5.4
     хяналтын төрлийг ЯГ ДӨРВӨӨР жагсаасан: шат шатны · зорилтот ·
     байнгын (мониторинг) · ээлжит бус. «Төлөвлөгөөт» гэдэг нь ТӨРӨЛ биш
     ШИНЖ ЧАНАР (`xab.inspection.is_planned` туг) бөгөөд журмын 1.6.2 нь
     зорилтот хяналтыг «төлөвлөгөөт БУС байдлаар явагддаг» гэж заасан тул
     хоёулаа зэрэг сонгогдоход зөрчил үүснэ. Лавлахад `is_active=0`-оор
     идэвхгүй болгосон — хуучин бүртгэл нэрээ хэвээр хадгална. */
  {
    key: "monitoring",
    label: "Байнгын хяналт (мониторинг)",
    desc: "ХАБЭА инженер ээлжийн туршид (журам 2.8)",
    icon: "fa-eye",
    color: "#0F766E",
  },
  {
    // Журмын 1.5.4-ийн нэршил. Өмнө нь «Төлөвлөгөөт бус» гэж байсан нь
    // журамд байхгүй нэр байв.
    key: "unplanned",
    label: "Ээлжит бус",
    desc: "ХАБЭА хэлтсээс зохион байгуулна (журам 2.9.1)",
    icon: "fa-calendar-times",
    color: "#CA8A04",
  },
  {
    key: "by_directive",
    label: "Удирдамжийн дагуу",
    desc: "Үйлдвэрийн удирдамжаар",
    icon: "fa-clipboard-list",
    color: "#531DAB",
  },
  {
    key: "bn_directive",
    label: "БН-ийн удирдамжаар",
    desc: "БН-ийн дотоод удирдамжаар",
    icon: "fa-file-signature",
    color: "#08979C",
  },
];

// FA6 → FA5 нэрийн хөрвүүлэг. База (xab.inspection_type) хуучин FA6 нэртэй
// үлдсэн ч icon хоосон харагдахгүй байхын тулд hydrate үед хөрвүүлнэ.
const FA6_TO_FA5 = {
  "fa-archive": "fa-archive",
  "fa-calendar-times": "fa-calendar-times",
  "fa-chart-bar": "fa-chart-bar",
  "fa-check-circle": "fa-check-circle",
  "fa-exclamation-circle": "fa-exclamation-circle",
  "fa-info-circle": "fa-info-circle",
  "fa-radiation-alt": "fa-radiation",
  "fa-times-circle": "fa-times-circle",
  "fa-file-alt": "fa-file-alt",
  "fa-save": "fa-save",
  "fa-sitemap": "fa-sitemap",
  "fa-cogs": "fa-cogs",
  "fa-hand-paper": "fa-hand-paper",
  "fa-tasks": "fa-tasks",
  "fa-map-marker-alt": "fa-map-marker-alt",
  "fa-users": "fa-users",
  "fa-walking": "fa-walking",
  "fa-undo": "fa-undo",
  "fa-redo": "fa-redo",
  "fa-shield-alt": "fa-shield-alt",
  "fa-layer-group": "fa-layer-group",
  "fa-exclamation-triangle": "fa-exclamation-triangle",
  "fa-user-edit": "fa-user-edit",
  "fa-biohazard": "fa-biohazard",
  /* ⚠ FA6-д ЗӨВХӨН байдаг нэрс (2026-09-29-нд баазад 4 нь тааралдсан:
     icon огт гарахгүй байв). Локал Font Awesome нь 5.11.2 тул FA6-ийн
     шинэ нэршлийг FA5-ийн glyph руу буулгана. FA5-д АГУУЛГА нь байхгүй
     бол (`fa-stairs`) утга санаагаар хамгийн дүйх нэрийг авна. */
  "fa-shield-halved": "fa-shield-alt",
  "fa-building-shield": "fa-user-shield",
  "fa-calendar-xmark": "fa-calendar-times",
  "fa-stairs": "fa-layer-group",
  "fa-triangle-exclamation": "fa-exclamation-triangle",
  "fa-circle-exclamation": "fa-exclamation-circle",
  "fa-circle-check": "fa-check-circle",
  "fa-circle-info": "fa-info-circle",
  "fa-circle-xmark": "fa-times-circle",
  "fa-xmark": "fa-times",
  "fa-trash-can": "fa-trash",
  "fa-pen-to-square": "fa-edit",
  "fa-file-lines": "fa-file-alt",
  "fa-chart-column": "fa-chart-bar",
  "fa-magnifying-glass": "fa-search",
  "fa-helmet-safety": "fa-hard-hat",
  "fa-bars-progress": "fa-tasks",
  "fa-gears": "fa-cogs",
  "fa-person-walking": "fa-walking",
  "fa-floppy-disk": "fa-save",
  "fa-rotate-left": "fa-undo",
  "fa-rotate-right": "fa-redo",
};

export const normalizeFaIcon = (icon) => {
  const name = String(icon || "").trim();
  if (!name) return "";
  return FA6_TO_FA5[name] || name;
};

/**
 * Үзлэгийн төрлийн meta (label / icon / color / desc)-г key эсвэл id-гаар олох.
 * Жагсаалт, дэлгэрэнгүй зэрэг газарт нэгэн ижил харагдалт өгөхөд ашиглана.
 */
/**
 * СОНГОГДДОГГҮЙ ч БААЗАД хадгалагддаг төрлүүд.
 *
 * ⚠ `INSPECTION_TYPES` нь `hydrateInspectionTypes()`-ээр баазын мөрүүдээр
 *   БҮРЭН СОЛИГДДОГ. Тиймээс тэр массивт нэмсэн тогтмол утга hydration
 *   амжилттай болмогц АЛГА БОЛНО. Энд тусад нь барина.
 *
 * `violation` — зөрчлийн бүртгэл. Зөрчил нь шалгах хуудасгүй, дангаараа
 *   бүртгэгддэг тул хэрэглэгч төрөл СОНГОХГҮЙ; систем энэ кодыг өөрөө
 *   тавина. Гэвч жагсаалтад тэмдэг нь харагдах ёстой.
 */
export const EXTRA_TYPE_LABELS = {
  violation: {
    key: "violation",
    label: "Зөрчил",
    desc: "Шалгах хуудасгүй, дангаар бүртгэсэн зөрчил",
    icon: "fa-exclamation-triangle",
    color: "#B45309",
  },
};

export const getInspectionType = (keyOrId) => {
  if (keyOrId === null || keyOrId === undefined || keyOrId === "") return null;
  const v = String(keyOrId);
  return (
    INSPECTION_TYPES.find(
      (t) => String(t.key) === v || (t.id != null && String(t.id) === v),
    ) ||
    // Лавлахад байхгүй ч системийн тогтмол код байж болно
    EXTRA_TYPE_LABELS[v] ||
    null
  );
};

// ── DB-ээс hydrate (GET /xab/inspection-types) ──────────────────────────────
// Дээрх INSPECTION_TYPES нь offline / татаж чадаагүй үеийн fallback. hydrate
// амжилттай бол массивыг DB мөрүүдээр байрандаа шинэчилж, `id`-г онооно.
// Ингэснээр код нь string `key`-ээс гадна база дахь integer `id`-гаар лавлана.
let _typesHydrated = false;
/** `force` — лавлах зассаны дараа кэшийг хүчээр дахин татна (админ CRUD). */
export async function hydrateInspectionTypes(force = false) {
  if (_typesHydrated && !force) return INSPECTION_TYPES;
  try {
    const rows = await xabRefApi.getInspectionTypes();
    if (Array.isArray(rows) && rows.length) {
      const byCode = Object.fromEntries(
        INSPECTION_TYPES.map((t) => [t.key, t]),
      );
      const next = rows.map((r) => {
        const prev = byCode[r.code] || {};
        // `||` — база дээр хоосон мөр (null эсвэл "") байвал hardcoded
        // утга хэвээр үлдэнэ (icon/өнгө алга болохоос сэргийлнэ).
        return {
          ...prev,
          id: r.id, // ← база дахь id
          key: r.code,
          label: r.label || prev.label,
          desc: r.description || prev.desc,
          icon: normalizeFaIcon(r.icon) || prev.icon,
          color: r.color_hex || prev.color,
        };
      });
      // массивыг байрандаа солих — import хийсэн газрууд шинэ утга авна
      INSPECTION_TYPES.length = 0;
      INSPECTION_TYPES.push(...next);
    }
    _typesHydrated = true;
  } catch (e) {
    // DB бэлэн биш бол hardcoded fallback хэвээр үлдэнэ
  }
  return INSPECTION_TYPES;
}

// App ачаалахад нэг удаа татна
hydrateInspectionTypes();

/**
 * Объектын ангиллын сонгогч — журам 2.7.5.
 * ЗОРИЛТОТ хяналт сонгосон үед л гарна: журам зөвхөн зорилтот хяналтад хамрах
 * объект/ажлын төрлийг жагсаасан.
 */
function ObjectClassPicker({ value, onChange }) {
  const [rows, setRows] = React.useState([]);

  React.useEffect(() => {
    let alive = true;
    xabInspectionObjectApi
      .list()
      .then((r) => {
        if (alive) setRows(r?.items || []);
      })
      .catch(() => {
        if (alive) setRows([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  if (!rows.length) return null;

  // Сонгогдсон мөрийн БҮТЭН бичлэг — доор задлан харуулна
  const selected = rows.find((r) => String(r.id) === String(value)) || null;

  // Бүлгээр нь optgroup — 14 мөр урт тул сонгоход хялбар болно
  const byGroup = [];
  const seen = {};
  rows.forEach((r) => {
    const key = r.group_code || "other";
    if (!seen[key]) {
      seen[key] = { label: r.group_name || "Бусад", options: [] };
      byGroup.push(seen[key]);
    }
    seen[key].options.push({
      value: r.id,
      label: (r.clause_no ? r.clause_no + " · " : "") + r.name,
    });
  });

  return (
    <div style={{ marginTop: 10 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          color: "#5F5E5A",
          marginBottom: 4,
        }}
      >
        Объектын ангилал{" "}
        <span style={{ fontWeight: 400, color: "#9AA3B0" }}>
          (журам 2.7.5 — зорилтот хяналтад хамрагдах объект)
        </span>
      </div>
      <Select
        allowClear
        showSearch
        optionFilterProp="label"
        style={{ width: "100%" }}
        placeholder="Аль объект / ажлын төрөлд хамаарах вэ?"
        value={value || undefined}
        onChange={(v) => onChange?.(v || null)}
        options={byGroup}
      />

      {/* ── СОНГОСОН ОБЪЕКТ ЛАВЛАХАД ЯГ АЛЬ НЬ ВЭ ──────────────────────
          Сонгогч дээр урт нэр таслагдаж харагддаг тул «би яг алийг нь
          сонгосон юм бол» гэдэг эргэлзээ үүсдэг байв. Одоо журмын
          ЗААЛТЫН ДУГААР, бүлэг, бүтэн томьёолол, лавлахын кодыг задлан
          харуулна — шалгалт нь журмын аль заалтад тулгуурласныг маргаангүй
          болгоно (албан шаардлагын эрх зүйн үндэслэл). */}
      {selected ? (
        <div
          style={{
            marginTop: 8,
            border: "1px solid #E6E9EF",
            borderLeft: `4px solid ${selected.color_hex || "#531DAB"}`,
            borderRadius: 8,
            background: "#FCFDFE",
            padding: "8px 12px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
              marginBottom: 4,
            }}
          >
            <span
              style={{
                fontFamily: "monospace",
                fontWeight: 800,
                fontSize: 12,
                color: selected.color_hex || "#531DAB",
              }}
            >
              ХЭМАБМТ-06 · {selected.clause_no || "—"}
            </span>
            {selected.group_name ? (
              <span
                style={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  padding: "1px 8px",
                  borderRadius: 999,
                  background: (selected.color_hex || "#531DAB") + "18",
                  color: selected.color_hex || "#531DAB",
                }}
              >
                {selected.group_name}
              </span>
            ) : null}
            {selected.code ? (
              <span style={{ fontSize: 10.5, color: "#9AA3B0" }}>
                лавлах: {selected.code}
              </span>
            ) : null}
          </div>

          <div style={{ fontSize: 12.5, color: "#1F2430", lineHeight: 1.55 }}>
            {selected.name}
          </div>

          {selected.description ? (
            <div style={{ fontSize: 11.5, color: "#5A6478", marginTop: 3 }}>
              {selected.description}
            </div>
          ) : null}

          <div style={{ fontSize: 10.5, color: "#9AA3B0", marginTop: 5 }}>
            Эх сурвалж: Лавлах сан → «Объектын ангилал»
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function InspectionTypeSection({
  value,
  onChange,
  options,
  /** Объектын ангилал (xab.inspection_object.id) — зорилтот хяналтад */
  objectId,
  onObjectChange,
  /** Чухал хяналтын шалгах хуудасны хариулт (critical_control төрөлд) */
  criticalSheet,
  onCriticalSheetChange,
  /** Хуудасны толгойд харуулах хам сэдэв — { jobName, deptName, workerName } */
  criticalContext,
}) {
  const items = Array.isArray(options) && options.length ? options : INSPECTION_TYPES;
  return (
    <Card>
      <SectionTitle>Үзлэгийн төрөл</SectionTitle>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
          gap: 8,
        }}
      >
        {items.map((t) => {
          const active = value === t.key;
          // Төрөл бүр ЗААВАЛ icon-той харагдана — база дээр хоосон/буруу
          // нэртэй байвал ерөнхий icon-оор орлуулна.
          const icon = normalizeFaIcon(t.icon) || "fa-clipboard-check";
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => onChange?.(t.key)}
              style={{
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
                padding: "10px 12px",
                borderRadius: 10,
                border:
                  "1.5px solid " + (active ? t.color : t.color + "33"),
                background: active ? t.color + "1F" : "#fff",
                cursor: "pointer",
                transition: "all 0.15s",
                textAlign: "left",
              }}
            >
              <span
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 8,
                  background: active ? t.color : t.color + "26",
                  color: active ? "#fff" : t.color,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 14,
                  flexShrink: 0,
                }}
              >
                <i className={"fas " + icon} />
              </span>
              <div style={{ minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 12.5,
                    fontWeight: 700,
                    color: active ? t.color : "#2A2F3A",
                    lineHeight: 1.2,
                  }}
                >
                  {t.label}
                </div>
                <div
                  style={{
                    fontSize: 10.5,
                    color: "#8A94A6",
                    lineHeight: 1.3,
                    marginTop: 2,
                  }}
                >
                  {t.desc}
                </div>
              </div>
            </button>
          );
        })}
      </div>
      {onObjectChange && value === "targeted" ? (
        <ObjectClassPicker value={objectId} onChange={onObjectChange} />
      ) : null}

      {/* «Чухал хяналт» сонгосон үед — 15 критик хяналтын ШАЛГАХ ХУУДАС.
          (Чухал хяналт.vsdx → үлгэрчилсэн загвар; өндөр эрсдэлтэй ажил
          гүйцэтгэхийн өмнө бөглөнө.) */}
      {value === "critical_control" ? (
        <CriticalControlLauncher
          value={criticalSheet}
          onChange={onCriticalSheetChange}
          context={criticalContext}
        />
      ) : null}
    </Card>
  );
}

/* ────────────────────────────────────────────────────────────────────────
 *  CriticalControlLauncher — «Чухал хяналтын хуудас бөглөх» товч + дүн.
 *  onCriticalSheetChange өгөөгүй бол зөвхөн харах горимд ажиллана
 *  (маягт нь хуудсыг хадгалахгүй ч ажилтан шалгаж үзэх боломжтой).
 * ──────────────────────────────────────────────────────────────────────── */
function CriticalControlLauncher({ value, onChange, context }) {
  const [open, setOpen] = React.useState(false);
  const s = value?.summary;
  const stopped = !!s && s.stops > 0;

  return (
    <div style={{ marginTop: 10 }}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 14px",
          borderRadius: 8,
          border: "1.5px solid #A32D2D",
          background: value ? "#A32D2D" : "#fff",
          color: value ? "#fff" : "#A32D2D",
          fontSize: 12.5,
          fontWeight: 700,
          cursor: "pointer",
        }}
      >
        <i className="fas fa-shield-alt" />
        {value ? "Чухал хяналтын хуудас — харах / засах" : "Чухал хяналтын хуудас бөглөх"}
      </button>

      {s ? (
        <div style={{ marginTop: 6, fontSize: 12, color: "#5A6478" }}>
          Бөглөсөн <b>{s.checked}</b>/{s.total} асуумж ·{" "}
          {stopped ? (
            <b style={{ color: "#C62828" }}>
              Ажил зогсоох эрх эдэлсэн ({s.stops})
            </b>
          ) : (
            <b style={{ color: "#16A34A" }}>Хэвийн — ажлаа гүйцэтгэнэ</b>
          )}
        </div>
      ) : (
        <div style={{ marginTop: 6, fontSize: 11.5, color: "#8A94A6" }}>
          Өндөр эрсдэлтэй ажил гүйцэтгэхийн өмнө 15 критик хяналтыг шалгана.
        </div>
      )}

      <CriticalControlSheet
        open={open}
        value={value}
        readOnly={!onChange}
        context={context || {}}
        onClose={() => setOpen(false)}
        onSubmit={(res) => {
          onChange?.(res);
          setOpen(false);
        }}
      />
    </div>
  );
}
